import Icon from 'Components/Icon'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Const from 'Utils/Consts'
import { useNavigate, useParams } from 'react-router-dom'
import AcceptRequestButton from './AcceptRequestButton'
import RejectRequestButton from './RejectRequestButton'
import { useAppDispatch, useAppSelector } from 'Store/selectors'
import { GetVisitRequestByNotificationId } from 'Application/Visits/Actions/action'
import CircularProgressbar from 'Components/CircularProgressbar'
import CancelRequestButton from './CancelRequestButton'
import StatusHelperText from './StatusHelperText'
import { AssignedVisitRequestStatus, VisitRequestStatus } from 'Application/Visits/Actions/enums'

function NotificationInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstance = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const dispatch = useAppDispatch()

  const { selectedItem: data, loading } = useAppSelector(s => s.visits.dispatchRequestList)

  useEffect(() => {
    dispatch(GetVisitRequestByNotificationId(id))
  }, [dispatch, id])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initializeMap = () => {
    if (!mapRef.current || !(window as any).L) return

    mapInstance.current = new (window as any).L.Map(mapRef.current, {
      key: Const.env.REACT_APP_NESHAN_MAP_API_KEY,
      maptype: 'dreamy',
      center: [data?.latitude ?? 35.6892, data?.longitude ?? 51.389],
      zoom: 17,
    })

    markerRef.current = (window as any).L.marker(
      [data?.latitude ?? 35.6892, data?.longitude ?? 51.389],
      {
        draggable: false,
      },
    ).addTo(mapInstance.current)

    return () => {
      mapInstance.current?.remove()
    }
  }

  useEffect(() => {
    initializeMap()

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [initializeMap])

  if (loading) {
    return (
      <div className="flex items-center justify-center grow">
        <CircularProgressbar />
      </div>
    )
  }

  if (!data) {
    return (
      <>
        <div className="flex justify-between items-center h-16 px-6 shrink-0">
          <div className="flex items-center gap-2">
            <div onClick={() => navigate(-1)}>
              <Icon name="arrow-right" className="w-6 h-6 text-gray-700 cursor-pointer" />
            </div>
            <div className="text-lg font-bold pl-2">{t('DISPATCH_DETAIL')}</div>
          </div>
        </div>
      </>
    )
  }

  const details: { key: string; value: string; wrap?: boolean; isHidden?: boolean }[] = [
    {
      key: 'نوع درخواست',
      value: 'اعزام کارشناس',
    },
    {
      key: 'نام مشتری',
      value: data.fullName,
    },
    {
      key: 'کد ملی مشتری',
      value: data.nationalCode,
    },
    {
      key: 'موبایل مشتری',
      value: data.mobilePhoneNumber,
    },
    {
      key: 'مشخصات خودرو',
      value: `${data.vehicleBrandTitle} - ${data.vehicleTipTitle} - ${data.yearOfConstruction}`,
    },
    {
      key: 'شماره انتظامی',
      value:
        data.plateCityCode +
        ' ایران ' +
        data.plateCarSerial +
        data.plateChar +
        data.plateCarOtherNumber,
    },
    {
      key: 'موعد بازدید',
      value: data.persianScheduleTime ?? '',
      isHidden: !data.persianScheduleTime,
    },
    {
      key: 'آدرس بازدید',
      value: data.address,
      wrap: true,
    },
  ]

  return (
    <div className="flex flex-col grow overflow-auto">
      <div className="flex justify-between items-center h-16 px-6 shrink-0">
        <div className="flex justify-between items-end gap-2">
          <div onClick={() => navigate('/Notification')}>
            <Icon name="arrow-right" className="w-6 h-6 text-gray-700" />
          </div>
          <div className="text-lg font-bold pl-2 ">{t('bodyDamageReport.requestDetails')}</div>
        </div>
      </div>

      <div className="flex flex-col grow overflow-auto px-4">
        {details.map(item => {
          if (item.isHidden) return null
          return (
            <div
              key={item.key}
              className={`flex justify-between py-1 gap-2 ${item.wrap ? 'flex-col items-start' : 'flex-row items-center'}`}
            >
              <span className="text-sm text-gray-600 whitespace-nowrap">{t(item.key)}:</span>
              <span className="text-sm font-semibold text-gray-800">{item.value}</span>
            </div>
          )
        })}

        {data.latitude && data.longitude ? (
          <div className="border border-gray-300 border-dashed h-72 rounded-lg">
            <div ref={mapRef} style={{ width: '100%', height: '300px', zIndex: 10 }} />
          </div>
        ) : (
          'نقشه وجود ندارد'
        )}
      </div>

      <div className="flex flex-col px-4 mt-auto gap-2 py-2">
        {data.assignedVisitRequestStatus === 2 ? (
          <CancelRequestButton visitRequestId={data.id} isEnabled={data.canCancel} />
        ) : (
          <>
            <AcceptRequestButton
              scheduleTime={data.scheduleTime}
              visitRequestId={data.id}
              isEnabled={
                data.assignedVisitRequestStatus === AssignedVisitRequestStatus.Registered &&
                (data.status === VisitRequestStatus.NotAssigned ||
                  data.status === VisitRequestStatus.CancelledByExpert)
              }
            />
            <RejectRequestButton
              visitRequestId={data.id}
              isEnabled={
                data.assignedVisitRequestStatus === AssignedVisitRequestStatus.Registered &&
                (data.status === VisitRequestStatus.NotAssigned ||
                  data.status === VisitRequestStatus.CancelledByExpert)
              }
            />
          </>
        )}
      </div>

      <StatusHelperText
        assignedVisitRequestStatus={data.assignedVisitRequestStatus}
        status={data.status}
      />
    </div>
  )
}

export default NotificationInfo
